// src/app/utils/file-guards.ts
export const ALLOWED_EXTENSIONS = ['.pdf', '.png', '.jpg'] as const;
export const ALLOWED_MIME = [
  'application/pdf',
  'image/png',
  'image/jpeg',
] as const;
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // keep in sync with backend

export function getExt(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : '';
}

export function isAllowedExtension(file: File): boolean {
  return ALLOWED_EXTENSIONS.includes(getExt(file.name) as any);
}

export function isAllowedSize(file: File): boolean {
  return file.size > 0 && file.size <= MAX_UPLOAD_BYTES;
}

// Magic bytes: %PDF, PNG signature, JPEG FF D8 FF
export async function hasAllowedSignature(file: File): Promise<boolean> {
  const buf = await file.slice(0, 12).arrayBuffer();
  const h = new Uint8Array(buf);

  const isPdf =
    h.length >= 4 &&
    h[0] === 0x25 &&
    h[1] === 0x50 &&
    h[2] === 0x44 &&
    h[3] === 0x46;

  const isPng =
    h.length >= 8 &&
    h[0] === 0x89 &&
    h[1] === 0x50 &&
    h[2] === 0x4e &&
    h[3] === 0x47 &&
    h[4] === 0x0d &&
    h[5] === 0x0a &&
    h[6] === 0x1a &&
    h[7] === 0x0a;

  const isJpg =
    h.length >= 3 && h[0] === 0xff && h[1] === 0xd8 && h[2] === 0xff;

  return isPdf || isPng || isJpg;
}

// Optional: cap pixel dimensions to avoid huge images (DoS-ish)
// 10k x 10k or 40MP limit as an example.
export async function withinImagePixelLimits(file: File): Promise<boolean> {
  if (!file.type.startsWith('image/')) return true;
  const bmp = await createImageBitmap(file).catch(() => null);
  if (!bmp) return false;
  const ok =
    bmp.width > 0 &&
    bmp.height > 0 &&
    bmp.width <= 10000 &&
    bmp.height <= 10000 &&
    bmp.width * bmp.height <= 40_000_000;
  bmp.close();
  return ok;
}

export async function validateUpload(
  file: File | null
): Promise<{ ok: boolean; error?: string }> {
  if (!file) {
    return { ok: false, error: 'No file selected.' };
  }
  if (!isAllowedExtension(file)) {
    return {
      ok: false,
      error: `Only ${ALLOWED_EXTENSIONS.join(', ')} files are allowed.`,
    };
  }
  if (!isAllowedSize(file)) {
    return {
      ok: false,
      error: `Max upload size is ${Math.floor(
        MAX_UPLOAD_BYTES / (1024 * 1024)
      )} MB.`,
    };
  }
  if (!(await hasAllowedSignature(file))) {
    return { ok: false, error: 'Unsupported or unrecognized file content.' };
  }
  if (!(await withinImagePixelLimits(file))) {
    return { ok: false, error: 'Image dimensions are too large.' };
  }
  return { ok: true };
}
